/* ============================================================
   section-testride.js
   Header "Book a test ride" store-finder drawer.
   - Opens from the desktop edge tab [data-eb-testride-open] or any element with that attr
     (e.g. the mobile nav-drawer CTA).
   - Filters dealers by model (multi-select), country, city.
   - Edge tab one-time amber pulse on scroll.
   Self-contained custom element; no dependency on EbHeader internals.
   ============================================================ */

class EbTestRideDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.eb-testride-drawer__panel');
    this.openers = [...document.querySelectorAll('[data-eb-testride-open]')];
    this.lastFocus = null;

    this.bindOpenClose();
    this.bindFilters();
    this.bindEdgeTabPulse();
  }

  /* ---- open / close ---- */
  bindOpenClose() {
    this.openers.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(btn);
      })
    );

    this.querySelectorAll('[data-eb-testride-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close())
    );

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trapFocus(e);
    });
  }

  open(trigger) {
    // close header menus / mobile drawer if open
    const header = document.querySelector('eb-header');
    if (header) {
      header.classList.remove('is-drawer-open');
      header.querySelectorAll('[aria-expanded="true"]').forEach((el) => {
        if (!this.contains(el)) el.setAttribute('aria-expanded', 'false');
      });
    }

    this.lastFocus = trigger || document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => {
      this.panel?.querySelector('button, select, input, a')?.focus();
    }, 60);
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  }

  trapFocus(e) {
    if (!this.classList.contains('is-open')) return;
    const focusable = [
      ...this.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---- filtering: model (multi) + country + city ---- */
  bindFilters() {
    this.selectedModels = new Set();
    this.selectedCity = 'all';

    this.modelOpts = [...this.querySelectorAll('[data-eb-tr-model]')];
    this.country = this.querySelector('[data-eb-tr-country]');
    this.cityChips = [...this.querySelectorAll('[data-eb-tr-city]')];
    this.stores = [...this.querySelectorAll('[data-eb-tr-store]')];
    this.countEl = this.querySelector('[data-eb-tr-count]');
    this.emptyEl = this.querySelector('[data-eb-tr-empty]');

    const mSelect = this.querySelector('[data-eb-model-select]');
    const mToggle = this.querySelector('[data-eb-model-toggle]');
    const mMenu = this.querySelector('[data-eb-model-menu]');
    this.mValue = this.querySelector('[data-eb-model-value]');
    this.allModelsLabel = this.mValue ? this.mValue.textContent.trim() : 'All models';

    // dropdown open/close
    if (mToggle && mMenu && mSelect) {
      mToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = mSelect.classList.toggle('is-open');
        mMenu.hidden = !open;
        mToggle.setAttribute('aria-expanded', String(open));
      });
      document.addEventListener('click', (e) => {
        if (!mSelect.contains(e.target)) {
          mSelect.classList.remove('is-open');
          mMenu.hidden = true;
          mToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // model checkboxes
    this.modelOpts.forEach((opt) =>
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        const key = opt.dataset.ebTrModel;
        const checked = opt.classList.toggle('is-checked');
        opt.setAttribute('aria-selected', String(checked));
        if (checked) this.selectedModels.add(key);
        else this.selectedModels.delete(key);
        this.updateModelLabel();
        this.applyFilters();
      })
    );

    // city chips
    this.cityChips.forEach((chip) =>
      chip.addEventListener('click', () => {
        if (chip.hidden) return;
        this.cityChips.forEach((c) => c.classList.remove('is-selected'));
        chip.classList.add('is-selected');
        this.selectedCity = chip.dataset.ebTrCity;
        this.applyFilters();
      })
    );

    // country drives which city chips show
    this.country?.addEventListener('change', () => {
      this.syncCityChips();
      this.applyFilters();
    });

    this.updateModelLabel();
    this.syncCityChips();
    this.applyFilters();
  }

  updateModelLabel() {
    if (!this.mValue) return;
    const names = this.modelOpts
      .filter((o) => o.classList.contains('is-checked'))
      .map((o) => o.querySelector('.eb-model-option__name')?.textContent || '');
    if (names.length === 0) {
      this.mValue.textContent = this.allModelsLabel;
    } else if (names.length === 1) {
      this.mValue.textContent = names[0];
    } else {
      this.mValue.innerHTML =
        names.length +
        ' selected<span class="eb-model-select__count">' +
        names.length +
        '</span>';
    }
  }

  syncCityChips() {
    const c = this.country?.value || '';
    this.cityChips.forEach((chip) => {
      if (chip.dataset.ebTrCity === 'all') {
        chip.hidden = false;
        return;
      }
      chip.hidden = c !== '' && chip.dataset.country !== c;
    });
    const active = this.cityChips.find((ch) => ch.classList.contains('is-selected'));
    if (active && active.hidden) {
      this.cityChips.forEach((x) => x.classList.remove('is-selected'));
      this.cityChips[0]?.classList.add('is-selected');
      this.selectedCity = 'all';
    }
  }

  applyFilters() {
    const c = this.country?.value || '';
    let shown = 0;
    this.stores.forEach((s) => {
      const matchCountry = c === '' || s.dataset.country === c;
      const matchCity = this.selectedCity === 'all' || s.dataset.city === this.selectedCity;
      const storeModels = (s.dataset.models || '').split(/\s+/);
      const matchModel =
        this.selectedModels.size === 0 ||
        storeModels.includes('all') ||
        [...this.selectedModels].some((m) => storeModels.includes(m));
      const ok = matchCountry && matchCity && matchModel;
      s.hidden = !ok;
      if (ok) shown += 1;
    });
    if (this.countEl) this.countEl.textContent = shown;
    if (this.emptyEl) this.emptyEl.hidden = shown !== 0;
  }

  /* ---- edge tab one-time pulse on scroll ---- */
  bindEdgeTabPulse() {
    const tab = document.querySelector('.eb-testride-tab');
    if (!tab) return;
    let pulsed = false;
    const onScroll = () => {
      if (pulsed) return;
      if (window.scrollY > 600) {
        pulsed = true;
        tab.classList.add('is-pulsing');
        window.setTimeout(() => tab.classList.remove('is-pulsing'), 3000);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}

if (!customElements.get('eb-testride-drawer')) {
  customElements.define('eb-testride-drawer', EbTestRideDrawer);
}
